import React, { useEffect, useRef } from 'react';
import { useRequestData } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';

import Slider from 'react-slick';
import PropTypes from 'prop-types';

import { galleryTrackingEvents } from '../../utilities/tracking';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import CarouselButton from './CarouselButton';
import VehicleImageCount from './VehicleImageCount';
import ImageDisplay from './ImageDisplay';
import MyCarsSaveIcon from '../gallery/MyCarsSaveIcon';
import useHorizontalLayout from '../../hooks/useHorizontalLayout';
import {
	resetWIAPIContent,
	replaceCarouselImageWithHTML
} from '../../utilities/wiapi-html';
import { REACT_SLICK_SPEED } from '../../global-constants';
import { useGalleryContext } from '../../contexts/GalleryContext';

const ImageSlider = ({ sliderRef, widgetElement }) => {
	const { state, actions, meta } = useGalleryContext();
	const { openModal, carouselIndex, imagesToDisplay } = state;
	const { setCarouselIndex, onOpenModal } = actions;
	const { isDesktop, galleryToolbarPlacement, imgTitle, prefs } = meta;
	const { galleryToolbarStyle = 'text', showMyCarsSave } = prefs;

	// Handle undefined preference - default to 'false' if not set
	const isShowSaveBtn = showMyCarsSave === 'true';

	const { widgetName, windowId } = useRequestData();
	const actionRef = useRef(null);
	const touchStart = useRef(null);
	const touchEnd = useRef(null);
	const beforeChangeTimer = useRef(null);

	const isMobile = !isDesktop;
	const TOTAL_IMAGES = imagesToDisplay.length;
	const lastImageIndex = imagesToDisplay.length - 1;
	const isHorizontalLayout = useHorizontalLayout();

	const showArrows = !isMobile && TOTAL_IMAGES > 1;
	const isOverlayMode = galleryToolbarStyle === 'none';

	const isSaveStandalone =
		isShowSaveBtn && !isMobile && !isOverlayMode && !openModal;

	const getMoveToPhotoText = (index) => {
		return TOTAL_IMAGES === index + 1
			? 'Move to last photo'
			: `Move to photo ${index + 1}`;
	};

	const handleTouchStart = (e) => {
		touchStart.current = e.touches[0].clientX;
		touchEnd.current = null;
	};

	const handleTouchEnd = () => {
		if (!touchStart.current || !touchEnd.current) return;
		const swipeDistance = touchStart.current - touchEnd.current;
		if (swipeDistance > 50) {
			actionRef.current = 'swipeLeft';
		} else if (swipeDistance < -50) {
			actionRef.current = 'swipeRight';
		}
	};

	const onNext = () => {
		const nextIndex =
			carouselIndex + 1 > lastImageIndex ? 0 : carouselIndex + 1;
		galleryTrackingEvents.clickNextButton(widgetName, windowId, {
			result: getMoveToPhotoText(nextIndex),
			currentItem: carouselIndex + 1,
			totalItems: TOTAL_IMAGES
		});
		sliderRef.current.slickNext();
	};

	const onPrev = () => {
		const nextIndex =
			carouselIndex - 1 < 0 ? lastImageIndex : carouselIndex - 1;
		galleryTrackingEvents.clickPrevButton(widgetName, windowId, {
			result: getMoveToPhotoText(nextIndex),
			currentItem: carouselIndex + 1,
			totalItems: TOTAL_IMAGES
		});
		sliderRef.current.slickPrev();
	};

	const onPhotoClick = (imageItem) => {
		if (imageItem.type === 'html' && widgetElement) {
			resetWIAPIContent(widgetElement);
			replaceCarouselImageWithHTML(widgetElement, imageItem);
		} else if (onOpenModal) {
			onOpenModal();
			galleryTrackingEvents.openModal(widgetName, windowId, {
				currentItem: carouselIndex + 1,
				totalItems: TOTAL_IMAGES
			});
		}
	};

	const mediaCount = TOTAL_IMAGES;

	// Fix adaptiveHeight for modal initial slide - wait for image to load then set height
	useEffect(() => {
		if (!openModal || !sliderRef.current) return undefined;

		const adjustHeightFrameId = requestAnimationFrame(() => {
			const slickList = sliderRef.current?.innerSlider?.list;
			if (!slickList) return;

			const currentSlide = slickList.querySelector(
				`[data-index="${carouselIndex}"]`
			);
			if (!currentSlide) return;

			const img = currentSlide.querySelector('img');
			if (!img) return;

			const setHeight = () => {
				const height = currentSlide.offsetHeight;
				if (height > 0) {
					slickList.style.height = `${height}px`;
				}
			};

			if (img.complete) {
				setHeight();
			} else {
				img.onload = setHeight;
			}
		});

		return () => cancelAnimationFrame(adjustHeightFrameId);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on modal toggle; other deps are DOM refs
	}, [openModal]);

	const settings = {
		className: 'carousel',
		infinite: true,
		speed: REACT_SLICK_SPEED,
		lazyLoad: false,
		slidesToShow: 1,
		slidesToScroll: 1,
		adaptiveHeight: true,
		arrows: showArrows,
		initialSlide: carouselIndex,
		prevArrow: (
			<CarouselButton
				direction="left"
				clickEvent={onPrev}
				mouseUpEvent={(e) => e.target.blur()}
				classes={['previous']}
			/>
		),
		nextArrow: (
			<CarouselButton
				direction="right"
				clickEvent={onNext}
				mouseUpEvent={(e) => e.target.blur()}
				classes={['next']}
			/>
		),
		beforeChange: (_current, next) => {
			if (isMobile) {
				// Cancel any pending timeout from a previous beforeChange
				// to prevent stale setCarouselIndex from overwriting current
				// index (causes ping-pong during fast VoiceOver navigation).
				if (beforeChangeTimer.current) {
					clearTimeout(beforeChangeTimer.current);
				}
				beforeChangeTimer.current = setTimeout(() => {
					beforeChangeTimer.current = null;
					setCarouselIndex(next);
				}, REACT_SLICK_SPEED);
			} else {
				setCarouselIndex(next);
			}

			resetWIAPIContent(widgetElement);
		}
	};

	const slides = imagesToDisplay.map((imageItem, index) => {
		const imageComponent = (
			<ImageDisplay
				image={imageItem}
				index={index}
				key={imageItem.src}
				keyName="gallery-carousel"
				onClick={() => onPhotoClick(imageItem)}
				imgTitle={imgTitle}
				carouselIndex={carouselIndex}
			/>
		);
		return imageComponent;
	});

	let positionToolbar;
	let styleImageCount;
	if (openModal) {
		positionToolbar =
			'pointer-events-none position-absolute ddc-push6 mt-4 translate-left-50 btn-unstyled text-muted';
	} else {
		positionToolbar =
			galleryToolbarPlacement === 'top'
				? 'absolute-top ml-4 mt-4'
				: 'absolute-bottom ml-4 mb-4';
		// eslint-disable max-len
		styleImageCount =
			'vehicle-gallery-toolbar vehicle-gallery-toolbar-bg ' +
			'text-white font-weight-bold border-radius-sm py-2 px-3 ddc-font-size-xsmall border-0 text-body';
	}

	useEffect(() => {
		if (
			actionRef.current === 'swipeLeft' ||
			actionRef.current === 'swipeRight'
		) {
			galleryTrackingEvents.swipeCarousel(widgetName, windowId, {
				result: getMoveToPhotoText(carouselIndex),
				currentItem: carouselIndex,
				totalItems: TOTAL_IMAGES,
				carouselDirection:
					actionRef.current === 'swipeLeft' ? 'next' : 'previous'
			});
		}
		actionRef.current = null;
		// eslint-disable-next-line react-hooks/exhaustive-deps -- other deps are stable refs/constants
	}, [carouselIndex, TOTAL_IMAGES]);

	if (TOTAL_IMAGES === 0) {
		return null;
	}

	return (
		<div
			className={`slider-container position-relative mx-auto ${
				isHorizontalLayout ? 'horizontal-layout' : ''
			}`}
			data-testid="slider-container"
			onTouchStart={handleTouchStart}
			onTouchMove={(e) => {
				touchEnd.current = e.touches[0].clientX;
			}}
			onTouchEnd={handleTouchEnd}
		>
			{/* Mobile modal ADA support:
				 - Hide react-slick DOM from SRs because cloned slides + transforms
				   break VoiceOver backward swipe navigation.
				 - Provide one stable focus target above the slider.
				 - Use focusable non-interactive element (`div`, role="img") so
				   VoiceOver announces photo info, not "button". */}
			<div aria-hidden={openModal && isMobile ? 'true' : undefined}>
				<Slider ref={sliderRef} {...settings}>
					{slides}
				</Slider>
			</div>
			{openModal && isMobile && (
				<div
					className="btn p-0 border-0 bg-transparent position-absolute pointer-events-none w-100 h-100"
					style={{ top: 0, left: 0 }}
					role="img"
					aria-roledescription="photo"
					aria-label={`${imgTitle || 'Vehicle photo'}, ${
						carouselIndex + 1
					} / ${TOTAL_IMAGES}`}
				/>
			)}
			{openModal && !isMobile ? (
				<VehicleImageCount
					className={setClassNames([
						positionToolbar,
						styleImageCount
					])}
					mediaCount={mediaCount}
					imageIndex={carouselIndex + 1}
					onClick={() => {
						onOpenModal();
						galleryTrackingEvents.clickPhotoCountToolbar(
							widgetName,
							windowId,
							{
								elementCTA: `${
									carouselIndex + 1
								} / ${TOTAL_IMAGES}`
							}
						);
					}}
				/>
			) : (
				''
			)}
			{isSaveStandalone && (
				<div className="gallery-mycars-save-icon absolute-bottom absolute-right mx-4 mb-4">
					<MyCarsSaveIcon />
				</div>
			)}
		</div>
	);
};

ImageSlider.propTypes = {
	// eslint-disable-next-line react/forbid-prop-types
	sliderRef: PropTypes.object,
	widgetElement: PropTypes.instanceOf(Element)
};

export default ImageSlider;
